import React, { useState, useEffect } from 'react';
import { API_URL } from '../../config/api';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Send, Check, Printer, RefreshCw, Download, Trash2 } from 'lucide-react';
import { Button } from '../../components/ui/button';

const InvoiceDetail = () => {
  const { invoiceId } = useParams();
  const navigate = useNavigate();
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  useEffect(() => {
    fetchInvoice();
  }, [invoiceId]);

  const fetchInvoice = async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/invoices/${invoiceId}`);
      const data = await res.json();
      setInvoice(data.invoice);
    } catch (err) {
      console.error('Failed to fetch invoice:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSend = async () => {
    setSending(true);
    try {
      await fetch(`${API_URL}/api/admin/invoices/${invoiceId}/send`, { method: 'POST' });
      fetchInvoice();
    } catch (err) {
      console.error('Failed to send invoice:', err);
    } finally {
      setSending(false);
    }
  };

  const handleMarkPaid = async () => {
    try {
      await fetch(`${API_URL}/api/admin/invoices/${invoiceId}/status?status=paid`, { method: 'PUT' });
      fetchInvoice();
    } catch (err) {
      console.error('Failed to update status:', err);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/invoices/${invoiceId}`, { method: 'DELETE' });
      if (res.ok) {
        navigate('/admin/invoices');
      }
    } catch (err) {
      console.error('Failed to delete invoice:', err);
    } finally {
      setDeleting(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
  };

  const getStatusBadge = (status) => {
    const styles = {
      draft: 'bg-gray-100 text-gray-600',
      sent: 'bg-blue-100 text-blue-700',
      paid: 'bg-green-100 text-green-700',
      overdue: 'bg-red-100 text-red-700',
      cancelled: 'bg-gray-100 text-gray-500'
    };
    return (
      <span className={`px-3 py-1 rounded-full text-sm font-medium ${styles[status] || styles.draft}`}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  // Check if we have detailed line items
  const hasLineItems = invoice?.line_items_detail && invoice.line_items_detail.length > 0;

  if (loading) {
    return (
      <div className="p-6 lg:p-8">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 rounded w-48"></div>
          <div className="h-96 bg-gray-200 rounded"></div>
        </div>
      </div>
    );
  }

  if (!invoice) {
    return (
      <div className="p-6 lg:p-8">
        <p className="text-gray-500">Invoice not found</p>
      </div>
    );
  }

  return (
    <div className="p-6 lg:p-8 max-w-4xl">
      {/* Header */}
      <div className="mb-6">
        <button onClick={() => navigate('/admin/invoices')} className="flex items-center gap-2 text-gray-500 hover:text-gray-700 mb-4">
          <ArrowLeft className="w-4 h-4" />
          Back to Invoices
        </button>
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">{invoice.invoice_number}</h1>
            <p className="text-gray-500">{formatDate(invoice.invoice_date)}</p>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            {getStatusBadge(invoice.status)}
            {invoice.status === 'draft' && (
              <Button onClick={handleSend} disabled={sending} className="bg-blue-600 hover:bg-blue-700" data-testid="send-invoice-btn">
                <Send className="w-4 h-4 mr-2" />
                {sending ? 'Sending...' : 'Send Invoice'}
              </Button>
            )}
            {invoice.status === 'sent' && (
              <Button onClick={handleSend} disabled={sending} variant="outline" data-testid="resend-invoice-btn">
                <RefreshCw className="w-4 h-4 mr-2" />
                {sending ? 'Sending...' : 'Resend'}
              </Button>
            )}
            {(invoice.status === 'sent' || invoice.status === 'overdue') && (
              <Button onClick={handleMarkPaid} className="bg-green-600 hover:bg-green-700" data-testid="mark-paid-btn">
                <Check className="w-4 h-4 mr-2" />
                Mark as Paid
              </Button>
            )}
            <Button variant="outline" onClick={() => window.open(`${API_URL}/api/admin/invoices/${invoiceId}/download-pdf`, '_blank')}>
              <Download className="w-4 h-4 mr-2" />
              Download PDF
            </Button>
            <Button variant="outline" onClick={() => window.print()}>
              <Printer className="w-4 h-4 mr-2" />
              Print
            </Button>
          </div>
        </div>
      </div>

      {/* Invoice Preview */}
      <div className="bg-white rounded-xl border border-gray-200 p-8 print:shadow-none print:border-none">
        {/* Invoice Header */}
        <div className="flex justify-between items-start mb-8">
          <div>
            <img 
              src="https://customer-assets.emergentagent.com/job_flats-clean-service/artifacts/2b1a8yim_Screenshot_20260123_012606_Drive.jpg" 
              alt="Just Cleaned" 
              className="h-10 mb-4"
            />
            <p className="text-sm text-gray-500">Just Cleaned Ltd</p>
            <p className="text-sm text-gray-500">contact@justcleaned.co.uk</p>
          </div>
          <div className="text-right">
            <h2 className="text-3xl font-bold text-gray-900 mb-2">INVOICE</h2>
            <p className="text-gray-600">{invoice.invoice_number}</p>
          </div>
        </div>

        {/* Bill To & Invoice Details */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
          <div>
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">Bill To</p>
            <p className="font-semibold text-gray-900">{invoice.client_company_name}</p>
            <p className="text-gray-600">{invoice.invoice_addressed_to || invoice.billing_contact_name}</p>
            <p className="text-gray-600">{invoice.billing_email}</p>
          </div>
          <div className="md:text-right">
            <div className="space-y-1">
              <p className="text-sm"><span className="text-gray-500">Invoice Date:</span> <span className="font-medium">{formatDate(invoice.invoice_date)}</span></p>
              <p className="text-sm"><span className="text-gray-500">Due Date:</span> <span className="font-medium">{formatDate(invoice.due_date)}</span></p>
              <p className="text-sm"><span className="text-gray-500">Payment Terms:</span> <span className="font-medium">{invoice.payment_terms} days</span></p>
            </div>
          </div>
        </div>

        {/* Site Details */}
        <div className="bg-gray-50 rounded-lg p-4 mb-4">
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1">Site</p>
          <p className="font-semibold">{invoice.site_name}</p>
          <p className="text-gray-600 text-sm">{invoice.site_address}</p>
        </div>

        {/* Works Period */}
        {invoice.works_period_text && (
          <p className="text-sm text-gray-500 italic mb-6">{invoice.works_period_text}</p>
        )}

        {/* Line Items Table */}
        <table className="w-full mb-8">
          <thead>
            <tr className="border-b-2 border-gray-200">
              <th className="text-left py-3 text-sm font-semibold text-gray-600">Description</th>
              {hasLineItems && (
                <>
                  <th className="text-center py-3 text-sm font-semibold text-gray-600 w-20">Qty</th>
                  <th className="text-right py-3 text-sm font-semibold text-gray-600 w-24">Unit Price</th>
                </>
              )}
              <th className="text-right py-3 text-sm font-semibold text-gray-600 w-24">Amount</th>
            </tr>
          </thead>
          <tbody>
            {hasLineItems ? (
              // Detailed line items view
              <>
                {invoice.line_items_detail.map((item, index) => (
                  <tr key={index} className="border-b border-gray-100">
                    <td className="py-4">
                      <p className="font-medium">{item.description}</p>
                    </td>
                    <td className="py-4 text-center text-gray-600">{item.quantity}</td>
                    <td className="py-4 text-right text-gray-600">£{item.unit_price.toFixed(2)}</td>
                    <td className="py-4 text-right font-medium">£{item.total.toFixed(2)}</td>
                  </tr>
                ))}
              </>
            ) : (
              // Legacy single item view
              <tr className="border-b border-gray-100">
                <td className="py-4" colSpan={hasLineItems ? 1 : 1}>
                  <p className="font-medium">{invoice.service_description}</p>
                  <p className="text-sm text-gray-500">Period: {invoice.period_start} to {invoice.period_end}</p>
                </td>
                <td className="py-4 text-right font-medium">£{invoice.net_amount.toFixed(2)}</td>
              </tr>
            )}
            
            {/* Additional Charges and Discounts */}
            {invoice.additional_charges && invoice.additional_charges.map((charge, index) => {
              const isDiscount = charge.amount < 0;
              return (
                <tr key={`charge-${index}`} className="border-b border-gray-100">
                  <td className="py-3 text-gray-700" colSpan={hasLineItems ? 3 : 1}>{charge.description}</td>
                  <td className={`py-3 text-right ${isDiscount ? 'text-red-600' : ''}`}>
                    {isDiscount ? `-£${Math.abs(charge.amount).toFixed(2)}` : `£${charge.amount.toFixed(2)}`}
                  </td>
                </tr>
              );
            })}
            
            {/* Subtotal if there are additional items */}
            {((invoice.additional_charges && invoice.additional_charges.length > 0) || invoice.vat_applicable) && (
              <tr className="border-b border-gray-100 bg-gray-50">
                <td className="py-3 text-gray-600 font-medium" colSpan={hasLineItems ? 3 : 1}>Subtotal</td>
                <td className="py-3 text-right font-medium">
                  £{(invoice.net_amount + (invoice.additional_charges_total || 0)).toFixed(2)}
                </td>
              </tr>
            )}
            
            {/* VAT */}
            {invoice.vat_applicable && (
              <tr className="border-b border-gray-100">
                <td className="py-3 text-gray-600" colSpan={hasLineItems ? 3 : 1}>VAT ({invoice.vat_rate}%)</td>
                <td className="py-3 text-right">£{invoice.vat_amount.toFixed(2)}</td>
              </tr>
            )}
          </tbody>
        </table>

        {/* Total */}
        <div className="flex justify-end mb-4">
          {invoice.deposit_paid > 0 ? (
            <div className="text-right">
              <div className="mb-2">
                <p className="text-sm text-gray-500">Invoice Total</p>
                <p className="text-xl font-semibold text-gray-700">£{invoice.total_amount.toFixed(2)}</p>
              </div>
              <div className="mb-2 text-green-600">
                <p className="text-sm">Less: Deposit Received</p>
                <p className="text-xl font-semibold">-£{invoice.deposit_paid.toFixed(2)}</p>
              </div>
              <div className="bg-[#1a1f4e] text-white rounded-lg px-6 py-4">
                <p className="text-sm opacity-80">Balance Due</p>
                <p className="text-3xl font-bold">£{invoice.balance_due.toFixed(2)}</p>
              </div>
            </div>
          ) : (
            <div className="bg-[#1a1f4e] text-white rounded-lg px-6 py-4">
              <p className="text-sm opacity-80">Total Due</p>
              <p className="text-3xl font-bold">£{invoice.total_amount.toFixed(2)}</p>
            </div>
          )}
        </div>
        
        {/* VAT Notice */}
        {!invoice.vat_applicable && (
          <p className="text-right text-sm text-gray-500 italic mb-8">VAT not applicable (not VAT registered)</p>
        )}

        {/* Payment Details */}
        <div className="border border-gray-200 rounded-lg p-4">
          <p className="font-semibold mb-2">Payment Details</p>
          <div className="text-sm text-gray-600 space-y-1">
            <p><strong>Bank:</strong> Metro Bank</p>
            <p><strong>Account Name:</strong> Just Cleaned Limited</p>
            <p><strong>Sort Code:</strong> 23-05-80</p>
            <p><strong>Account Number:</strong> 43563539</p>
            <p><strong>Reference:</strong> {invoice.invoice_number}</p>
          </div>
        </div>

        {/* Footer */}
        <div className="mt-8 pt-4 border-t border-gray-100 text-center text-sm text-gray-500">
          <p>Thank you for your business.</p>
          <p className="mt-1">Just Cleaned Ltd · contact@justcleaned.co.uk</p>
        </div>
      </div>

      {/* Danger Zone */}
      <div className="bg-white rounded-2xl border border-red-200 p-6 mt-6">
        <h3 className="text-lg font-semibold text-red-700 mb-4">Danger Zone</h3>
        {!showDeleteConfirm ? (
          <Button
            variant="outline"
            className="border-red-300 text-red-600 hover:bg-red-50"
            onClick={() => setShowDeleteConfirm(true)}
          >
            <Trash2 className="w-4 h-4 mr-2" />
            Delete Invoice
          </Button>
        ) : (
          <div className="flex items-center gap-4">
            <p className="text-sm text-gray-600">Are you sure? This cannot be undone.</p>
            <Button
              variant="outline"
              onClick={() => setShowDeleteConfirm(false)}
            >
              Cancel
            </Button>
            <Button
              className="bg-red-600 hover:bg-red-700 text-white"
              onClick={handleDelete}
              disabled={deleting}
            >
              {deleting ? 'Deleting...' : 'Yes, Delete'}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default InvoiceDetail;
